import { toast } from "sonner";

export interface OCCWell {
    id: string;
    api: string;
    name: string;
    operator: string;
    status: string;
    formattedStatus: 'active' | 'inactive' | 'unknown';
    type: string;
    lat: number;
    lng: number;
}

// Use direct absolute URL - Testing if OCC supports CORS directly (most public GIS do)
const OCC_LAYER_URL = 'https://gis.occ.ok.gov/server/rest/services/Hosted/RBDMS_WELLS/FeatureServer/220/query';


export const occWellService = {
    /**
     * Fetches wells from OCC public GIS server within the given bounding box
     */
    fetchNearbyWells: async (bounds: google.maps.LatLngBounds): Promise<OCCWell[]> => {
        try {
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();

            // ArcGIS Geometry: xmin,ymin,xmax,ymax
            const geometry = `${sw.lng()},${sw.lat()},${ne.lng()},${ne.lat()}`;

            const params = new URLSearchParams({
                f: 'json',
                returnGeometry: 'true',
                spatialRel: 'esriSpatialRelIntersects',
                geometry: geometry,
                geometryType: 'esriGeometryEnvelope',
                inSR: '4326', // Request input spatial reference (WGS84)
                outFields: 'api,well_name,operator,wellstatus,welltype',
                outSR: '4326', // Request output spatial reference (WGS84)
                resultRecordCount: '500' // Limit to prevent overwhelming the client
            });

            const response = await fetch(`${OCC_LAYER_URL}?${params.toString()}`);

            if (!response.ok) {
                throw new Error(`OCC Server Error: ${response.statusText}`);
            }

            const data = await response.json();

            if (data.error) {
                toast.error("Failed to fetch competitor data from OCC");
                return [];
            }

            if (!data.features) return [];

            return data.features.map((f: any) => {
                const attr = f.attributes;

                // Robust field extraction managing potential case sensitivity issues
                // Based on API response: name="well_name", alias="WELL_NAME"
                const wellName = attr.well_name || attr.WELL_NAME || attr.WellName || 'Unknown Well';
                const operator = attr.operator || attr.OPERATOR || attr.OperatorName || 'Unknown Operator';
                const statusRaw = (attr.wellstatus || attr.WELLSTATUS || attr.status || attr.Status || '').toUpperCase();
                const wellType = attr.welltype || attr.WELLTYPE || attr.WellType || 'Unknown';
                const rawApi = attr.api || attr.API || '';

                // Map raw status to simplified internal status
                // AC = Active, PA = Plugged/Abandoned, DRY = Dry Hole, SI = Shut In
                let formattedStatus: OCCWell['formattedStatus'] = 'unknown';
                if (statusRaw === 'AC' || statusRaw.includes('ACTIVE') || statusRaw.includes('PRODUCING')) formattedStatus = 'active';
                else if (statusRaw === 'PA' || statusRaw === 'DRY' || statusRaw.includes('PLUGGED')) formattedStatus = 'inactive';

                // Format API to string if it's a number
                const apiString = String(rawApi);

                return {
                    id: `occ-${apiString}`,
                    api: apiString,
                    name: wellName,
                    operator: operator,
                    status: statusRaw,
                    formattedStatus,
                    type: wellType,
                    lat: f.geometry.y,
                    lng: f.geometry.x
                };
            });

        } catch (error: any) {
            console.error('Error in fetchNearbyWells:', error);
            // Return empty array on failure as requested (no mock data)
            toast.error(`Failed to load competitor data: ${error.message || 'Unknown error'}`);
            return [];
        }
    }
};
